/**
 * Unique persist keys for Nischit people data.
 * Lead form / Redux keep DDM fieldReferences (fullName, dateOfBirth, …).
 * Journey stages persist role-prefixed keys so quote/summary/payment never collide.
 */

export const PROPOSER_BASIC_FORM_TO_PERSIST = {
  mobileNumber: "proposerMobileNumber",
  mobileNo: "proposerMobileNumber",
  mobile: "proposerMobileNumber",
  dateOfBirth: "proposerDob",
  dob: "proposerDob",
  liDob: "proposerDob",
  occupation: "proposerOccupation",
  annualIncome: "proposerAnnualIncome",
  annIncome: "proposerAnnualIncome",
  gender: "proposerGender",
};

export const PROPOSER_PERSONAL_FORM_TO_PERSIST = {
  fullName: "proposerName",
  name: "proposerName",
  lifeAssuredName: "proposerName",
  emailAddress: "proposerEmailAddress",
  email: "proposerEmailAddress",
  emailId: "proposerEmailAddress",
};

export const PRIMARY_ANNUITANT_FORM_TO_PERSIST = {
  primaryAnnuitantName: "primaryAnnuitantName",
  annuitantRelationship: "primaryAnnuitantRelationship",
  annuitantDateOfBirth: "primaryAnnuitantDob",
  primaryAnnuitantDob: "primaryAnnuitantDob",
  annuitantGender: "primaryAnnuitantGender",
  primaryAnnuitantGender: "primaryAnnuitantGender",
};

export const SECONDARY_ANNUITANT_KEYS = [
  "secondaryAnnuitantName",
  "secondaryAnnuitantDob",
  "secondaryAnnuitantGender",
  "secondaryAnnuitantRelation",
  "secondaryAnnuitantRelationBizCode",
];

const PASSTHROUGH_LEAD_KEYS = new Set([
  "currentStage",
  "productLob",
  "planName",
  "annualIncomeBiCode",
  "annualIncomeRangeLabel",
  "utmSource",
  "utmMedium",
  "utmCampaign",
  "isSinglePay",
  "enableSinglePayCondition",
  "nationality",
  "educationalLevel",
  "residencyPinCode",
  "pinCode",
  "consentToTerms",
]);

export function remapFields(source, mapping) {
  if (!source || typeof source !== "object") return {};
  const out = {};
  Object.entries(source).forEach(([key, value]) => {
    if (value === undefined) return;
    if (PASSTHROUGH_LEAD_KEYS.has(key) && !mapping[key]) {
      out[key] = value;
      return;
    }
    const persistKey = mapping[key] || key;
    if (out[persistKey] === undefined || out[persistKey] === null || out[persistKey] === "") {
      out[persistKey] = value;
    }
  });
  return out;
}

export function pickSecondaryAnnuitantFields(source) {
  if (!source || typeof source !== "object") return {};
  const nested =
    source?.annuitant?.secondaryAnnuitant &&
    typeof source.annuitant.secondaryAnnuitant === "object"
      ? source.annuitant.secondaryAnnuitant
      : {};
  const merged = { ...nested, ...source };
  const out = {};
  SECONDARY_ANNUITANT_KEYS.forEach((key) => {
    const value = merged[key];
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    out[key] = value;
  });
  return out;
}

export function resolveSecondaryAnnuitantQuoteFields(
  quoteResult,
  customizePlanData,
) {
  return pickSecondaryAnnuitantFields({
    ...(customizePlanData || {}),
    ...(quoteResult || {}),
    ...(quoteResult?.biPayload || {}),
  });
}

const planNameLooksJoint = (value) =>
  String(value || "")
    .toLowerCase()
    .includes("joint");

/** Proposal shows Secondary Annuitant when quote is joint life or secondary details already exist. */
export function isNischitJointLifeSelected(quoteResult, customizePlanData) {
  if (
    planNameLooksJoint(quoteResult?.selectedPlan) ||
    planNameLooksJoint(quoteResult?.biPayload?.selectedPlan) ||
    planNameLooksJoint(customizePlanData?.selectedPlan)
  ) {
    return true;
  }
  return (
    Object.keys(resolveSecondaryAnnuitantQuoteFields(quoteResult, customizePlanData))
      .length > 0
  );
}

/**
 * Copy unique persist keys onto DDM / Redux aliases so lead form, quote UI,
 * and existing API readers keep working without duplicate persist.
 */
export function hydrateFormAliasesFromRoleKeys(data) {
  if (!data || typeof data !== "object") return data;
  const next = { ...data };

  const copyIfEmpty = (alias, ...canonical) => {
    if (next[alias] != null && String(next[alias]).trim() !== "") return;
    for (const key of canonical) {
      if (next[key] != null && String(next[key]).trim() !== "") {
        next[alias] = next[key];
        return;
      }
    }
  };

  const preferCanonical = (alias, ...canonical) => {
    for (const key of canonical) {
      if (next[key] != null && String(next[key]).trim() !== "") {
        next[alias] = next[key];
        return;
      }
    }
    copyIfEmpty(alias, ...canonical);
  };

  preferCanonical("dateOfBirth", "proposerDob");
  preferCanonical("dob", "proposerDob");
  preferCanonical("mobileNumber", "proposerMobileNumber");
  preferCanonical("mobileNo", "proposerMobileNumber");
  preferCanonical("gender", "proposerGender");
  preferCanonical("occupation", "proposerOccupation");
  preferCanonical("annualIncome", "proposerAnnualIncome");
  preferCanonical("fullName", "proposerName");
  preferCanonical("emailAddress", "proposerEmailAddress");
  preferCanonical("emailId", "proposerEmailAddress");
  preferCanonical("email", "proposerEmailAddress");

  preferCanonical("annuitantDateOfBirth", "primaryAnnuitantDob");
  preferCanonical("annuitantGender", "primaryAnnuitantGender");
  preferCanonical("annuitantRelationship", "primaryAnnuitantRelationship");

  return next;
}
